import { Injectable } from '@angular/core';
import { Category } from '../models/new/category';
import { Brand } from '../models/new/brand.model';
import { Gender } from '../models/new/style';
import { StyleGroup } from '../models/new/style-group';
import { Collection } from '../models/new/collection';
import { ExportChannel } from '../models/new/export-channel';
import { Shot, ShotType } from '../models/new/shot';
import { ExportTemplate, ExportTemplateFilters } from '../models/new/export-template';
import { ExportChannelMapper } from './export-channel-mapper';
import { TemplateMapper } from './template-mapper';

@Injectable({
  providedIn: 'root'
})
export class ExportTemplateMapper {
  constructor(
    private templateMapper: TemplateMapper,
    private exportChannelMapper: ExportChannelMapper
  ) {}

  toExportTemplateShotType(shotType: any) {
    return new ShotType({ id: shotType.id, name: shotType.name, code: shotType.code });
  }

  toExportTemplateShot(shot: any) {
    return new Shot({
      id: shot.id,
      shotType: shot.shotType ? this.toExportTemplateShotType(shot.shotType) : undefined,
      aspectRatio: shot.aspectRatio ? this.templateMapper.toAspectRatio(shot.aspectRatio) : undefined,
      mediaFormat: shot.mediaFormat ? this.templateMapper.toMediaFormat(shot.mediaFormat) : undefined,
      code: shot.code || undefined
    });
  }

  toExportChannel(node: any) {
    return this.exportChannelMapper.toExportChannel(node);
  }

  toExportTemplate(node: any) {
    return new ExportTemplate({
      id: node.id,
      categoryId: node.categoryId,
      isDefault: node.isDefault,
      active: node.active,
      brand: node.brand ? new Brand({ id: node.brand.id, name: node.brand.name }) : undefined,
      category: node.category ? new Category({ id: node.category.id, name: node.category.name }) : undefined,
      collection: node.collection ? new Collection({ id: node.collection.id, name: node.collection.name }) : undefined,
      gender: node.gender ? new Gender({ id: node.gender.id, name: node.gender.name }) : undefined,
      styleGroup: node.styleGroup ? new StyleGroup({ id: node.styleGroup.id, name: node.styleGroup.name }) : undefined,
      exportChannel: node.exportChannel ? this.toExportChannel(node.exportChannel) : undefined,
      shots: node.shots ? node.shots.map((s: any) => this.toExportTemplateShot(s)) : []
    });
  }

  toWhereClauseById(id: number) {
    return { id: { eq: id } };
  }

  toCodifiedUseCodes(nodes: any[]): string[] {
    const codes: string[] = [];

    (nodes || []).forEach((node: any) => {
      const code = node.codifiedUse?.code || node.code;
      if (code != null && code !== '') {
        codes.push(String(code));
      }
    });

    return codes;
  }

  toExportTemplateShotsQL(shots?: Shot[]) {
    return (shots || []).map((shot) => ({
      shotTypeId: shot.shotType?.id || undefined,
      aspectRatioId: shot.aspectRatio?.id || undefined,
      mediaFormatId: shot.mediaFormat?.id || undefined,

      code:
        Number(shot.code) < 10 ? '0' + shot.code : Number(shot.code) < 100 ? '' + shot.code : shot.code || undefined
    }));
  }

  toCreateExportTemplateQL(exportTemplate: ExportTemplate) {
    return {
      brandId: exportTemplate.brand?.id || undefined,
      categoryId: exportTemplate.category?.id || undefined,
      collectionId: exportTemplate.collection?.id || undefined,
      styleGroupId: exportTemplate.styleGroup?.id || undefined,
      genderId: exportTemplate.gender?.id || undefined,
      exportChannelId: exportTemplate.exportChannel?.id,
      shots: this.toExportTemplateShotsQL(exportTemplate.shots)
    };
  }

  toUpdateExportTemplateQL(exportTemplate: ExportTemplate) {
    return {
      id: exportTemplate.id || undefined,
      ...this.toCreateExportTemplateQL(exportTemplate)
    };
  }

  toDeleteExportTemplateQL(exportTemplateId: number) {
    return { exportCodifiedUseMapId: exportTemplateId };
  }

  toWhereClause(filters: ExportTemplateFilters) {
    return {
      brand: filters.brands?.length ? { id: { in: filters.brands.map((b) => b.id) } } : undefined,
      category: filters.categories?.length ? { id: { in: filters.categories.map((c) => c.id) } } : undefined,
      styleGroup: filters.styleGroups?.length ? { id: { in: filters.styleGroups.map((sg) => sg.id) } } : undefined,
      exportChannel: filters.exportChannelId ? { id: { eq: filters.exportChannelId } } : undefined
    };
  }

  filtersToUrl(filters: ExportTemplateFilters): any {
    return {
      brands: filters.brands ? filters.brands.map((brand) => brand.id) : undefined,
      categories: filters.categories ? filters.categories.map((category) => category.id) : undefined,
      styleGroups: filters.styleGroups ? filters.styleGroups.map((styleGroup) => styleGroup.id) : undefined,
      page: filters.page || 1,
      size: filters.size || 20
    };
  }

  urlToFilters(url: any) {
    return new ExportTemplateFilters({
      brands: url.brands
        ? Array.isArray(url.brands)
          ? url.brands.map((id: any) => new Brand({ id: Number(id) }))
          : [new Brand({ id: Number(url.brands) })]
        : undefined,
      categories: url.categories
        ? Array.isArray(url.categories)
          ? url.categories.map((id: any) => new Category({ id: Number(id) }))
          : [new Category({ id: Number(url.categories) })]
        : undefined,
      styleGroups: url.styleGroups
        ? Array.isArray(url.styleGroups)
          ? url.styleGroups.map((id: any) => new StyleGroup({ id: Number(id) }))
          : [new StyleGroup({ id: Number(url.styleGroups) })]
        : undefined,
      page: Number(url.page) || 1,
      size: Number(url.size) || 20
    });
  }
}
